export class Mutex {

    private mutex = Promise.resolve();

    lock(): PromiseLike<() => void> {
        let begin: (unlock: () => void) => void = unlock => { };

        this.mutex = this.mutex.then(() => {
            return new Promise(begin);
        });

        return new Promise(res => {
            begin = res;
        });
    }
}


type ReleaseFn = () => void;

/**
 * A class that provides per-key mutual exclusion.
 */
export class KeyedMutex<K = string> {
    private readonly locks = new Map<K, Promise<void>>();

    /**
     * Acquire a lock for a given key.
     *
     * Returns a Promise that resolves to a function `release()`.
     * You *must* call `release()` to release the lock.
     */
    public async lock(key: K): Promise<ReleaseFn> {
        // The current lock promise for this key, or a resolved promise if none.
        const currentLock = this.locks.get(key) ?? Promise.resolve();

        // This "lock acquisition" promise will complete after currentLock resolves
        // (i.e. we have the right to execute), and after we manually release it.
        let unlock: ReleaseFn;
        const waitingForLock = new Promise<void>(resolve => {
            unlock = resolve; // We'll call this to let the next waiter proceed
        });

        // Chain: The new lock for this key is currentLock => then waitingForLock
        const newLock = currentLock.then(() => waitingForLock);

        // Store the new lock chain for this key.
        this.locks.set(key, newLock);

        // Wait for the currentLock to finish before returning control
        // so your code runs only after the previous lock is truly free.
        await currentLock;

        // Now we've acquired the lock; we return a function that, when called,
        // will release the lock for the *next* waiter in the chain.
        return () => {
            unlock(); // Call the resolver we assigned above
        };
    }
}
